import React, { type HTMLAttributes } from 'react';
import classNames from 'classnames';

import { DynamicMediaItem } from '@uncinc/react-drupal-core';
import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';

import { Container, Icon, WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';

import useLayoutVariables from '../../../hooks/useLayoutVariables';
import type { ParagraphQuote as Type } from '../../../types/uncinc_profile/paragraphs';

import './style.scss';

interface ParagraphQuoteProps extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
  entity: ImmutableEntity<Type>;
}

const ParagraphQuote: React.FC<ParagraphQuoteProps> = ({ id, entity }) => {
  const {
    heroImageAspectRatio,
    imageColClasses,
    imageColClassesAlignmentRight,
    textColClassesWithImage,
    textColClassesWithImageAlignmentLeft,
    textColClassesWithoutImage,
  } = useLayoutVariables('paragraphQuote');

  const paragraphQuote = entity.toJS();
  const quote = paragraphQuote.field_body[0].processed;
  const authorName = paragraphQuote.field_name[0].value;
  const authorFunction = paragraphQuote.field_label[0]?.value;
  const authorProfilePicture = paragraphQuote.field_profile_picture[0];
  const alignment = paragraphQuote.field_alignment[0].value;
  const image = paragraphQuote.field_media_item[0];

  const containerClasses = classNames('Paragraph', 'ParagraphQuote', {
    'ParagraphQuote--flipped': alignment === 'right' && image,
  });

  const imageClasses = classNames(imageColClasses, {
    // For the Quote component, top and left are the same
    [imageColClassesAlignmentRight]: ['left', 'top'].includes(alignment),
  });

  const textClasses = classNames({
    [textColClassesWithImageAlignmentLeft]: ['right'].includes(alignment),
    [textColClassesWithImage]: image,
    [textColClassesWithoutImage]: !image,
  });

  return (
    <div id={id} className={containerClasses}>
      <Container>
        <div className="row">
          <div className={textClasses}>
            <div className="ParagraphQuote__text-wrapper">
              <div className="ParagraphQuote__text">
                <Icon className="ParagraphQuote__icon" name="quote" />

                <WysiwygField
                  className="ParagraphQuote__quote quote"
                  property="schema:text"
                  html={quote}
                />

                <div className="ParagraphQuote__author">
                  {authorProfilePicture && (
                    <DynamicMediaItem
                      className="ParagraphQuote__author__profile-picture"
                      media={authorProfilePicture}
                      imageAspectRatio="1_1"
                    />
                  )}

                  <div className="ParagraphQuote__author__info">
                    <h4 className="ParagraphQuote__author__info__name">
                      {authorName}
                    </h4>
                    <span className="ParagraphQuote__author__info__function">
                      {authorFunction}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {image && (
            <div className={imageClasses}>
              <div className="ParagraphQuote__image-wrapper">
                <DynamicMediaItem
                  className="ParagraphQuote__image"
                  media={image}
                  imageAspectRatio={heroImageAspectRatio}
                />
              </div>
            </div>
          )}
        </div>
      </Container>
    </div>
  );
};

export default ParagraphQuote;
